import { Component, Inject, OnInit } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { map, takeUntil } from 'rxjs/operators'
import { forkJoin, of } from 'rxjs'
import { difference } from 'lodash'

import { User, UserInList } from '@shared/models/user.model'
import { MessengerApiService } from '@app/modules/client/messenger/services/messenger-api.service'
import { DestroyService } from '@core/services/destroy.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { ChatModel, ChatUserModel } from '@shared/models/messenger/messenger.model'
import { AvatarSize } from '@shared/common-components/avatar/avatar.model'
import { RoleType } from '@app/core/types/role.type'
import { sameId } from '@core/helpers/same-id'

@Component({
  selector: 'lin-chat-create-form',
  templateUrl: './chat-create-form.component.html',
  styleUrls: ['./chat-create-form.component.scss'],
  providers: [DestroyService],
  standalone: false,
})
export class ChatCreateFormComponent implements OnInit {
  form: FormGroup
  searchValue = ''

  userList: UserInList[]
  filteredUserList: UserInList[]
  selectedUsers = new Map<string, UserInList | ChatUserModel>()

  AvatarSize = AvatarSize
  isLoading = true

  private get currentUser(): User {
    return this.authService.getCurrentUser()
  }

  private get userIds(): string[] {
    return <string[]>[...this.selectedUsers.keys()]
  }

  get isGroupChat(): boolean {
    return this.dialogData.scope === 'edit'
      ? !this.dialogData.chat?.isIndividual || this.chatScopeChanged || this.selectedUsers.size > 2
      : this.selectedUsers.size > 1
  }

  get chatTitle(): string {
    return (<string>this.form.get('chatTitle').value)?.trim()
  }

  get canKick(): boolean {
    return (
      this.dialogData.scope === 'create' ||
      (this.isGroupChat && (!(this.currentUser.role.name === RoleType.Driver) || this.isChatLeader(this.currentUser.id)))
    )
  }

  get chatScopeChanged(): boolean {
    if (this.dialogData.scope === 'create') return false

    const previousUsers = this.dialogData.chat.users.map(({ id }) => id)
    return !!difference(previousUsers, this.userIds).length || previousUsers.length !== this.userIds.length
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: { chat: ChatModel; scope: 'create' | 'edit' },
    private readonly dialogRef: MatDialogRef<ChatCreateFormComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly messengerApiService: MessengerApiService,
    private readonly destroyService: DestroyService,
    private readonly authService: AuthService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.initForm()
    this.initUserList()
    this.subscribeInputChange()
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      search: '',
      chatTitle: '',
    })
  }

  private initUserList(): void {
    const hideCurrentUser = (user: UserInList | ChatUserModel): boolean => !sameId(user.id, this.currentUser.id)

    if (this.dialogData?.chat) {
      this.form.get('chatTitle').setValue(this.dialogData.chat.name)
      this.dialogData.chat.users.every((user) => this.selectedUsers.set(user.id, user))
    }

    this.messengerApiService
      .getClientUsers(this.currentUser.team.clientId, ['picture', 'networkStatus'])
      .pipe(takeUntil(this.destroyService))
      .subscribe((userList) => {
        this.userList = userList.filter(hideCurrentUser)
        this.filteredUserList = this.userList.slice()
        this.isLoading = false
      })
  }

  private subscribeInputChange(): void {
    const setUserList = (userList: UserInList[]) => (this.filteredUserList = userList)

    this.form
      .get('search')
      .valueChanges.pipe(
        map((value: string | null): UserInList[] => (value ? this.filter(value) : this.userList?.slice())),
        takeUntil(this.destroyService),
      )
      .subscribe(setUserList)
  }

  private filter(value: string): UserInList[] {
    const filterByUserFullName = (user: UserInList): boolean => user.fullName.toLocaleLowerCase().includes(value)

    return this.userList.filter(filterByUserFullName)
  }

  createChat(): void {
    this.isLoading = true

    this.messengerApiService
      .createChat({ userIds: this.userIds, ...(this.isGroupChat && !!this.chatTitle && { name: this.chatTitle }) })
      .pipe(takeUntil(this.destroyService))
      .subscribe((chat: ChatModel): void => {
        this.isLoading = false
        this.dialogRef.close()
        void this.router.navigate(['/', 'client', 'messenger', `${chat.id}`])
      })
  }

  updateChat(): void {
    this.isLoading = true

    const chatUsers$ = this.chatScopeChanged
      ? this.messengerApiService.addRemoveUserToChat(this.dialogData.chat.id, {
          userIds: [...this.userIds, this.currentUser.id],
        })
      : of(null)
    const chatTitle$ =
      this.isGroupChat && this.chatTitle !== this.dialogData.chat.name
        ? this.messengerApiService.editChat(this.dialogData.chat.id, { name: this.chatTitle || null })
        : of(null)

    forkJoin([chatUsers$, chatTitle$])
      .pipe(takeUntil(this.destroyService))
      .subscribe(() => {
        this.isLoading = false
        this.dialogRef.close()
      })
  }

  handleUserClick(user: UserInList): void {
    if (this.dialogData?.chat?.users?.find((chatUser) => sameId(chatUser.id, user.id)) && !this.canKick) return

    this.selectedUsers.has(user.id) ? this.selectedUsers.delete(user.id) : this.selectedUsers.set(user.id, user)
  }

  isChatLeader(userId: string): boolean {
    if (!this.isGroupChat || !this.dialogData?.chat) return false

    const chatLeaderId = this.dialogData.chat.users?.find((user) => user.leader)?.id
    return sameId(userId, chatLeaderId)
  }
}
